import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { APP_TERMS_URL } from '@app/shared/utilities/app-info';
import { CaptchaFieldComponent } from '../../shared/captcha-field.component';

/**
 * Sign-up step 3: password, terms and security check.
 * The "Create account" button is the form's submit button, so the parent
 * form's (ngSubmit) handles the actual submission.
 */
@Component({
  selector: 'app-signup-step-three',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CaptchaFieldComponent],
  template: `
    <div class="auth__form" [formGroup]="formGroup">
      <div class="field">
        <label for="password">Password</label>
        <div class="password-wrap">
          <input
            id="password"
            [type]="showPassword ? 'text' : 'password'"
            class="form-control"
            formControlName="password"
            placeholder="At least 6 characters"
            autocomplete="new-password"
            [class.is-invalid]="isInvalid('password')"
          />
          <button
            type="button"
            class="password-toggle"
            (click)="showPassword = !showPassword"
            [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'"
          >
            <i class="fa" [class.fa-eye]="!showPassword" [class.fa-eye-slash]="showPassword" aria-hidden="true"></i>
          </button>
        </div>
        <p class="field-error" *ngIf="isInvalid('password')">Password must be at least 6 characters.</p>
      </div>

      <div class="field">
        <label for="confirmPassword">Confirm password</label>
        <div class="password-wrap">
          <input
            id="confirmPassword"
            [type]="showConfirmPassword ? 'text' : 'password'"
            class="form-control"
            formControlName="confirmPassword"
            placeholder="Re-enter your password"
            autocomplete="new-password"
            [class.is-invalid]="isInvalid('confirmPassword') || passwordMismatch"
          />
          <button
            type="button"
            class="password-toggle"
            (click)="showConfirmPassword = !showConfirmPassword"
            [attr.aria-label]="showConfirmPassword ? 'Hide password' : 'Show password'"
          >
            <i class="fa" [class.fa-eye]="!showConfirmPassword" [class.fa-eye-slash]="showConfirmPassword" aria-hidden="true"></i>
          </button>
        </div>
        <p class="field-error" *ngIf="isInvalid('confirmPassword')">Please confirm your password.</p>
        <p class="field-error" *ngIf="!isInvalid('confirmPassword') && passwordMismatch">Passwords do not match.</p>
      </div>

      <app-captcha-field
        [question]="captchaQuestion"
        inputId="signupCaptcha"
        [invalid]="isInvalid('captchaInput')"
      ></app-captcha-field>

      <div class="field">
        <label class="check" for="termsAccepted">
          <input type="checkbox" id="termsAccepted" formControlName="termsAccepted" />
          <span>
            I agree to the
            <a class="auth__link" [href]="termsUrl" target="_blank" rel="noopener">Terms and Conditions</a>
          </span>
        </label>
        <p class="field-error" *ngIf="isInvalid('termsAccepted')">You must accept the terms and conditions.</p>
      </div>

      <div class="auth__actions">
        <button type="button" class="btn-auth btn-auth--ghost" (click)="previousStep.emit()" [disabled]="isLoading">
          <i class="fa fa-arrow-left" aria-hidden="true"></i>
          <span>Back</span>
        </button>
        <button type="submit" class="btn-auth" [disabled]="!isStepValid() || isLoading" (click)="touchAll()">
          <span>{{ isLoading ? 'Creating account…' : 'Create account' }}</span>
          <i class="fa" [class.fa-arrow-right]="!isLoading" [class.fa-spinner]="isLoading" [class.fa-spin]="isLoading" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  `
})
export class SignupStepThreeComponent {
  @Input({ required: true }) formGroup!: FormGroup;
  @Input() isLoading = false;
  @Input({ required: true }) captchaQuestion = '';
  @Output() previousStep = new EventEmitter<void>();

  readonly termsUrl = APP_TERMS_URL;
  showPassword = false;
  showConfirmPassword = false;

  private readonly controls = ['password', 'confirmPassword', 'termsAccepted', 'captchaInput'];

  get passwordMismatch(): boolean {
    return !!this.formGroup.errors?.['passwordMismatch'] && !!this.formGroup.get('confirmPassword')?.touched;
  }

  isInvalid(name: string): boolean {
    const control = this.formGroup.get(name);
    return !!(control?.invalid && control.touched);
  }

  isStepValid(): boolean {
    return this.controls.every((name) => this.formGroup.get(name)?.valid) && !this.formGroup.errors?.['passwordMismatch'];
  }

  touchAll(): void {
    this.controls.forEach((name) => this.formGroup.get(name)?.markAsTouched());
  }
}
